import { SupportedMarketKey } from '@nxt-insurance/constants'

import {
  PET_HEALTH_V2_DEFAULT_CONFIGURATION_PARAMETERS,
  PET_HEALTH_V2_PRODUCT_KEY,
} from '@flows/configs/petHealthV2/constants'
import { pricingSchema, schema } from '@flows/configs/petHealthV2/schema'
import { getNativeSteps, getWebSteps } from '@flows/configs/petHealthV2/steps'
import { getProductFlowConfiguration } from '@flows/configs/shared/flowConfiguration'
import type { FlowConfigProps, StepConfigParams } from '@flows/configs/types'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'
import { generateGetFlowValueFunctions } from '@flows/insurance/utils'

const FIRST_STEP_NAMES = { native: 'pet_name', web: 'pet_details' } as const

const SCHEMAS = { SCHEMA: schema, PRICING_SCHEMA: pricingSchema }

interface PetHealthV2FlowConfigProps extends FlowConfigProps {
  platform: 'native' | 'web'
}

const getInitialValues = () => ({
  [PET_HEALTH_V2_PRODUCT_KEY]: {
    productKey: PET_HEALTH_V2_PRODUCT_KEY,
    configuration_parameters: PET_HEALTH_V2_DEFAULT_CONFIGURATION_PARAMETERS,
  },
})

const getStepParams = ({ clientHelpers, subFlow = SubFlow.PetHealth }: FlowConfigProps) =>
  ({
    productKey: PET_HEALTH_V2_PRODUCT_KEY,
    clientHelpers,
    helperFunctions: generateGetFlowValueFunctions(PET_HEALTH_V2_PRODUCT_KEY),
    subFlow,
    market: SupportedMarketKey.De,
  }) satisfies StepConfigParams

const getNativeConfig = (props: FlowConfigProps) =>
  getProductFlowConfiguration(PET_HEALTH_V2_PRODUCT_KEY, getNativeSteps(getStepParams(props)), {
    constants: { FIRST_STEP_NAME: FIRST_STEP_NAMES.native, ...SCHEMAS },
    getInitialValues,
  })

const getWebConfig = (props: FlowConfigProps) =>
  getProductFlowConfiguration(PET_HEALTH_V2_PRODUCT_KEY, getWebSteps(getStepParams(props)), {
    constants: { FIRST_STEP_NAME: FIRST_STEP_NAMES.web, ...SCHEMAS },
    getInitialValues,
  })

function getPetHealthV2Config(
  props: PetHealthV2FlowConfigProps & { platform: 'native' }
): ReturnType<typeof getNativeConfig>
function getPetHealthV2Config(
  props: PetHealthV2FlowConfigProps & { platform: 'web' }
): ReturnType<typeof getWebConfig>
function getPetHealthV2Config({ platform, ...props }: PetHealthV2FlowConfigProps) {
  return platform === 'native' ? getNativeConfig(props) : getWebConfig(props)
}

export default getPetHealthV2Config
